import React, { useState, useRef, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core";
import {
  usePlByProgramStyles,
  PlFilterGroup,
  PlPageHeader,
  PlChartPanel,
  PL_CY_C,
  PL_BUD_C,
  PL_FONT,
  PL_TH_BG,
  PL_TH_TEXT,
  PL_TH_BORDER,
} from "./plByProgramTheme";
import { useReportFonts } from "../../reportFonts";
import { getPLMonthlyTrendAPI } from "../../../../api/reports-api";
import { toReportApiFilters } from "../filterRelationshipUtils";
import { getApiFilterKey, getGlobalPeriodColumnKeys, formatActualsThroughDate, headerActualsDate, getPeriodFilteredTotal } from "./plTimeFilterUtils";
import Loading from "../../../../common-components/Loading";
import { TH_ACTUAL_BG, TH_ACTUAL_TEXT } from "../../reportTableConfig";

const HAIRLINE     = "#E2E8F0";
const FORECAST_C   = "#2563EB";
const TEXT_MUTED   = "#6B7280";
const TEXT_PRIMARY = "#111827";
const ACCENT       = "#4F46E5";
const PY_BAR_C     = "#64748B";
const CUM_PY_C     = "#475569";
const CUM_CY_C     = "#818CF8";
const CUM_BUD_C    = "#FBBF24";
const ACTUAL_TH_BG = TH_ACTUAL_BG;
const ACTUAL_TH_FG = TH_ACTUAL_TEXT;

const MONTH_KEYS   = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const QUARTER_KEYS   = ["q1","q2","q3","q4"];
const QUARTER_LABELS = ["Q1","Q2","Q3","Q4"];

const FALLBACK_LINE_ITEMS = [
  { key: "contractRevenue", label: "Revenue", isPct: false },
  { key: "directLabor",     label: "Direct Labor",     isPct: false },
  { key: "subcontractor",   label: "Subcontractor",    isPct: false },
  { key: "odcs",            label: "ODCs",             isPct: false },
  { key: "totalDirectCost", label: "Total Direct Cost",isPct: false },
  { key: "grossMargin",     label: "Gross Margin",     isPct: false },
  { key: "grossMarginPct",  label: "Gross Margin %",   isPct: true  },
];

const METRIC_OPTS = ["CY","PY","Budget","Cumulative CY","Cumulative PY","Cumulative Budget"];

/* ── helpers ─────────────────────────────────────────────────── */
function cumArr(arr) {
  let s = 0;
  return arr.map((v) => { s += v; return +s.toFixed(2); });
}

function isAllZero(scenario, keys) {
  return keys.every((k) => !scenario?.[k]);
}

function buildApiFilters(filters) {
  return toReportApiFilters(filters, [
    "year", "orgId", "bu", "contractId", "projectId", "contractName", "customerName",
    "contractType", "clinLvl2", "businessType", "awardType", "competitionType",
    "popEndDate", "sector",
  ]);
}

function makeMonthCols(cutoff) {
  return [
    ...MONTH_KEYS.map((k, i) => ({ key: k, label: MONTH_LABELS[i], isActual: i + 1 <= cutoff })),
    { key: "total", label: "Total", isTotal: true },
  ];
}

function makeQtrCols(cutoff) {
  return [
    ...QUARTER_KEYS.map((k, i) => ({ key: k, label: QUARTER_LABELS[i], isActual: cutoff >= (i + 1) * 3 })),
    { key: "total", label: "Total", isTotal: true },
  ];
}

function fmtCell(v, isPct) {
  if (v == null) return "—";
  if (isPct) return `${(v * 100).toFixed(1)}%`;
  if (v === 0) return "—";
  const abs = Math.abs(v).toLocaleString("en-US", { maximumFractionDigits: 0 });
  return v < 0 ? `($${abs})` : `$${abs}`;
}

/* ── chart option ────────────────────────────────────────────── */
function buildOption(apiData, lineItem, selMetrics, viewMode, globalPeriodKeys) {
  if (!apiData?.items || !lineItem) return {};

  const { key: liKey, label: liLabel, isPct } = lineItem;
  const allPeriodKeys = viewMode === "Month" ? MONTH_KEYS : QUARTER_KEYS;
  const allCats       = viewMode === "Month" ? MONTH_LABELS : QUARTER_LABELS;
  const periodKeys    = globalPeriodKeys?.length ? globalPeriodKeys : allPeriodKeys;
  const cats          = globalPeriodKeys?.length
    ? periodKeys.map((k) => {
      const i = allPeriodKeys.indexOf(k);
      return i >= 0 ? allCats[i] : k;
    })
    : allCats;

  const getVals = (scenario) =>
    periodKeys.map((k) => {
      const v = scenario?.[k] ?? 0;
      return isPct ? +(v * 100).toFixed(2) : +(v / 1_000_000).toFixed(2);
    });

  const cyScen  = apiData.items[liKey]?.cy  ?? {};
  const pyScen  = apiData.items[liKey]?.py  ?? {};
  const budScen = apiData.items[liKey]?.bud ?? {};

  const cyVals  = getVals(cyScen);
  const pyVals  = getVals(pyScen);
  const budVals = getVals(budScen);

  const cyEmpty  = isAllZero(cyScen,  periodKeys);
  const pyEmpty  = isAllZero(pyScen,  periodKeys);
  const budEmpty = isAllZero(budScen, periodKeys);

  const fmtMoney = (v) => {
    const n = +v;
    const abs = Math.abs(n);
    if (abs >= 1000) return `$${(n / 1000).toFixed(1)}B`;
    if (abs >= 1)    return `$${n.toFixed(1)}M`;
    if (abs >= 0.001) return `$${(n * 1000).toFixed(1)}K`;
    return `$${n.toFixed(2)}M`;
  };
  const fmtV = (v) => (isPct ? `${(+v).toFixed(1)}%` : fmtMoney(v));

  const LABEL_BASE = 6;
  const LABEL_STEP = 16;
  const LABEL_CLUSTER_SPREAD = isPct ? 2 : 18;

  // Cumulative lines first so bar label placement can clear them.
  const PCT_SRC = {
    grossMarginPct: { num: "grossMargin", den: "contractRevenue" },
  };
  const pctSrc = PCT_SRC[liKey];
  const cumPctVals = (scen) => {
    if (!pctSrc) return null;
    const numScen = apiData.items[pctSrc.num]?.[scen] ?? {};
    const denScen = apiData.items[pctSrc.den]?.[scen] ?? {};
    let n = 0, d = 0;
    return periodKeys.map((k) => {
      n += numScen[k] ?? 0;
      d += denScen[k] ?? 0;
      return d ? +((n / d) * 100).toFixed(2) : 0;
    });
  };
  const cumVals = (scen, vals) => (isPct ? cumPctVals(scen) : cumArr(vals));

  const cumLines = [];
  if (!isPct || pctSrc) {
    if (selMetrics.includes("Cumulative PY") && !pyEmpty)
      cumLines.push({ name: `Cumulative PY ${liLabel}`, data: cumVals("py", pyVals), color: CUM_PY_C });
    if (selMetrics.includes("Cumulative CY") && !cyEmpty)
      cumLines.push({ name: `Cumulative CY ${liLabel}`, data: cumVals("cy", cyVals), color: CUM_CY_C });
    if (selMetrics.includes("Cumulative Budget") && !budEmpty)
      cumLines.push({ name: `Cumulative Budget ${liLabel}`, data: cumVals("bud", budVals), color: CUM_BUD_C });
  }
  const hasCumLines = cumLines.length > 0;

  const pointCount = cumLines[0]?.data?.length ?? 0;
  const labelConfig = cumLines.map(() => Array(pointCount).fill(null));
  /* Lift line labels above bar tops + bar data labels */
  const barSeriesCount =
    (selMetrics.includes("PY") && !pyEmpty ? 1 : 0) +
    (selMetrics.includes("CY") && !cyEmpty ? 1 : 0) +
    (selMetrics.includes("Budget") && !budEmpty ? 1 : 0);
  const lineClearBars = hasCumLines && barSeriesCount > 0 ? 56 : 0;

  const barValsForCumLine = (lineName) => {
    if (lineName.startsWith("Cumulative PY")) return pyVals;
    if (lineName.startsWith("Cumulative CY")) return cyVals;
    if (lineName.startsWith("Cumulative Budget")) return budVals;
    return null;
  };

  for (let i = 0; i < pointCount; i++) {
    const atIdx = cumLines
      .map((line, si) => ({ si, value: line.data[i], line }))
      .filter((x) => x.value != null && !Number.isNaN(x.value));

    if (atIdx.length === 0) continue;

    if (atIdx.length === 1) {
      /* First period (Jan/Q1) cum equals the bar — lift above bar labels so both show. */
      const matchesBar =
        barSeriesCount > 0 &&
        (() => {
          const bars = barValsForCumLine(atIdx[0].line.name);
          if (!bars) return false;
          const cumN = Number(atIdx[0].value);
          const barN = Number(bars[i]);
          return (
            Number.isFinite(cumN) &&
            Number.isFinite(barN) &&
            Math.abs(cumN - barN) <= 0.05
          );
        })();
      labelConfig[atIdx[0].si][i] = {
        position: "top",
        distance: LABEL_BASE + lineClearBars + (matchesBar ? 14 : 0),
      };
      continue;
    }

    const sortedAsc = [...atIdx].sort((a, b) => a.value - b.value);
    const spread = sortedAsc[sortedAsc.length - 1].value - sortedAsc[0].value;
    const clustered = spread <= LABEL_CLUSTER_SPREAD;

    sortedAsc.forEach((entry, rank) => {
      const total = sortedAsc.length;
      const step = clustered ? LABEL_STEP : Math.max(10, LABEL_STEP - 4);
      /* Never place line labels below the point when bars are present —
         that puts them into the bar body. */
      if (total >= 3 && rank === 0 && !clustered && barSeriesCount === 0) {
        labelConfig[entry.si][i] = {
          position: "bottom",
          distance: LABEL_BASE,
        };
      } else {
        labelConfig[entry.si][i] = {
          position: "top",
          distance: LABEL_BASE + lineClearBars + rank * step,
        };
      }
    });
  }

  const series = [];

  const BAR_LABEL_OVERLAP_BG = "rgba(237, 233, 254, 0.95)";
  const barSeriesDefs = [];
  if (selMetrics.includes("PY") && !pyEmpty)
    barSeriesDefs.push({ name: `PY ${liLabel}`, data: pyVals, color: PY_BAR_C });
  if (selMetrics.includes("CY") && !cyEmpty)
    barSeriesDefs.push({ name: `CY ${liLabel}`, data: cyVals, color: PL_CY_C });
  if (selMetrics.includes("Budget") && !budEmpty)
    barSeriesDefs.push({ name: `Budget ${liLabel}`, data: budVals, color: PL_BUD_C });

  /* Rank bars per period so close labels can be staggered upward */
  const barLabelDist = barSeriesDefs.map(() =>
    Array(periodKeys.length).fill(hasCumLines ? 10 : 4),
  );
  for (let i = 0; i < periodKeys.length; i++) {
    const atIdx = barSeriesDefs
      .map((s, si) => ({ si, value: Number(s.data[i]) }))
      .filter((x) => Number.isFinite(x.value) && x.value !== 0);
    if (atIdx.length <= 1) continue;
    const sortedAsc = [...atIdx].sort((a, b) => a.value - b.value);
    sortedAsc.forEach((entry, rank) => {
      /* When cum lines exist, lift bar labels well above the bars so they clear the line */
      barLabelDist[entry.si][i] = (hasCumLines ? 18 : 4) + rank * (hasCumLines ? 16 : 12);
    });
  }

  /*
   * Layering (higher zlevel paints on top):
   *   0 — cumulative line strokes
   *   1 — bars + bar labels (drawn above lines)
   *   2 — cumulative line labels
   */
  barSeriesDefs.forEach(({ name, data, color }, si) => {
    series.push({
      name,
      type: "bar",
      data: (data || []).map((v, i) => {
        const n = Number(v);
        return {
          value: v,
          label: {
            show: Number.isFinite(n) && n !== 0,
            position: "top",
            distance: barLabelDist[si][i],
            fontSize: 8,
            fontWeight: 700,
            fontFamily: PL_FONT,
            color,
            backgroundColor: hasCumLines ? BAR_LABEL_OVERLAP_BG : "rgba(255,255,255,0.95)",
            borderRadius: 8,
            padding: [2, 5],
            formatter: () => fmtV(n),
          },
        };
      }),
      itemStyle: { color },
      z: 2,
      zlevel: 1,
    });
  });

  cumLines.forEach((line) => {
    series.push({
      name: line.name,
      type: "line",
      data: line.data,
      lineStyle: { color: line.color, width: 2.5 },
      itemStyle: { color: line.color },
      symbol: "circle",
      symbolSize: 6,
      label: { show: false },
      z: 3,
      zlevel: 0,
    });
  });

  /* Line labels on a higher layer so they stay visible above bars */
  cumLines.forEach((line, si) => {
    series.push({
      name: `${line.name}·lbl`,
      type: "line",
      data: line.data.map((v, i) => {
        const cfg = labelConfig[si][i];
        const show = cfg != null && v != null && !Number.isNaN(Number(v));
        return {
          value: v,
          label: show
            ? {
              show: true,
              position: cfg.position,
              distance: cfg.distance,
              fontSize: 9,
              fontWeight: 700,
              fontFamily: PL_FONT,
              color: line.color,
              backgroundColor: "rgba(255,255,255,0.95)",
              borderRadius: 8,
              padding: [2, 5],
              formatter: () => fmtV(v),
            }
            : { show: false },
        };
      }),
      lineStyle: { width: 0, opacity: 0 },
      showSymbol: true,
      symbol: "circle",
      symbolSize: 1,
      itemStyle: { color: "transparent", borderWidth: 0 },
      tooltip: { show: false },
      silent: true,
      legendHoverLink: false,
      z: 20,
      zlevel: 2,
    });
  });

  const legendNames = [
    ...barSeriesDefs.map((s) => s.name),
    ...cumLines.map((l) => l.name),
  ];

  return {
    grid:    { top: hasCumLines ? 140 : 104, right: 24, bottom: 36, left: 16, containLabel: true },
    legend:  {
      top: 6, itemWidth: 10, itemHeight: 10, icon: "circle", itemGap: 10,
      data: legendNames,
      textStyle: { fontSize: 10, color: "#64748B", fontFamily: PL_FONT },
    },
    tooltip: {
      trigger: "axis", axisPointer: { type: "shadow" },
      backgroundColor: "#fff", borderColor: "#E2E8F0", borderWidth: 1,
      textStyle: { fontSize: 10, fontFamily: PL_FONT },
      formatter: (params) => {
        const visible = (params || []).filter((p) => p.seriesName && !String(p.seriesName).endsWith("·lbl"));
        const header = `<div style="font-weight:600;margin-bottom:6px;font-family:Roboto, Helvetica, Arial, sans-serif">${visible[0]?.axisValue ?? params[0]?.axisValue}</div>`;
        const rows = visible.map((p) => {
          const raw = Array.isArray(p.value) ? p.value[1] : p.value;
          const val = isPct ? `${(+raw).toFixed(1)}%` : fmtMoney(raw);
          return `<div style="display:flex;justify-content:space-between;gap:16px">${p.marker}<span>${p.seriesName}</span><b>${val}</b></div>`;
        }).join("");
        return header + rows;
      },
    },
    xAxis:   { type: "category", data: cats, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { fontSize: 10, color: "#94A3B8", fontFamily: PL_FONT } },
    yAxis:   {
      type: "value", axisLine: { show: false }, axisTick: { show: false },
      splitLine: { lineStyle: { color: "#F1F5F9", type: "dashed" } },
      axisLabel: { fontSize: 9, color: "#94A3B8", fontFamily: PL_FONT, formatter: (v) => (isPct ? `${v}%` : fmtMoney(v)) },
    },
    series,
  };
}

/* ── local styles ────────────────────────────────────────────── */
const useStyles = makeStyles(() => ({
  filterDivider: { width: 1, height: 28, background: "#E2E8F0", flexShrink: 0 },
  infoWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    "&:hover $infoText, &:focus-within $infoText": { display: "block" },
  },
  infoIcon: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    background: ACCENT,
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    border: "none",
    padding: 0,
    cursor: "help",
    fontFamily: PL_FONT,
  },
  infoText: {
    display: "none",
    position: "absolute",
    left: 0,
    top: "calc(100% + 6px)",
    zIndex: 5,
    background: "#1F2937",
    color: "#fff",
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: "nowrap",
    padding: "6px 10px",
    borderRadius: 6,
    pointerEvents: "none",
  },

  toggleWrap:  { display: "inline-flex", alignItems: "center", gap: 8, flexShrink: 0 },
  toggleLabel: { fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", transition: "color 0.2s" },
  toggleTrack: {
    position: "relative", width: 44, height: 22, borderRadius: 11,
    background: "#CBD5E1", cursor: "pointer", flexShrink: 0, border: "1px solid #B0BEC5",
  },
  toggleThumb: {
    position: "absolute", top: 2, width: 16, height: 16, borderRadius: "50%",
    background: PL_TH_BG, boxShadow: "0 1px 4px rgba(0,0,0,0.3)", transition: "left 0.2s",
  },

  msWrap:         { position: "relative", display: "inline-flex", flexShrink: 0 },
  msTrigger:      { display: "inline-flex", alignItems: "stretch", borderRadius: 28, overflow: "hidden", border: "1px solid #E2E8F0", cursor: "pointer", userSelect: "none", height: 36 },
  msTriggerLabel: { background: "#F0F2FF", color: "#4A55A2", fontSize: 10, fontWeight: 700, padding: "0 14px", whiteSpace: "nowrap", display: "flex", alignItems: "center", letterSpacing: "0.04em", textTransform: "uppercase" },
  msTriggerValue: { background: "#fff", fontSize: 13, fontWeight: 400, color: TEXT_PRIMARY, padding: "0 28px 0 14px", minWidth: 130, display: "flex", alignItems: "center", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23111827' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center", fontFamily: PL_FONT },
  msPanel:        { position: "absolute", top: "calc(100% + 4px)", left: 0, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.13)", zIndex: 9999, minWidth: 170, maxHeight: 260, overflowY: "auto", padding: "4px 0" },
  msItem:         { display: "flex", alignItems: "center", gap: 8, padding: "6px 14px", fontSize: 12, fontWeight: 500, color: TEXT_PRIMARY, cursor: "pointer", userSelect: "none", "&:hover": { background: "#F9FAFB" } },
  msDivider:      { height: 1, background: HAIRLINE, margin: "3px 0" },

  chartPanelTrend: { height: 440 },

  tableSection:      { marginBottom: 24, background: "#FFFFFF", borderRadius: 12, border: "1px solid #EBEBEB", boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)", overflow: "hidden" },
  tableSectionHead:  { padding: "20px 24px 16px", borderBottom: `1px solid ${HAIRLINE}` },
  tableSectionTitle: { fontSize: 15, fontWeight: 600, color: TEXT_PRIMARY, letterSpacing: "-0.02em", lineHeight: 1.35, fontFamily: PL_FONT, margin: 0 },
  tableSectionMeta:  { marginTop: 4, fontSize: 12, fontWeight: 400, color: TEXT_MUTED, fontFamily: PL_FONT, lineHeight: 1.4 },

  tableWrap: { overflowX: "auto", background: "#FFFFFF" },
  table:     { width: "100%", borderCollapse: "collapse", fontFamily: PL_FONT, fontSize: 13, tableLayout: "auto" },
  thead:     { position: "sticky", top: 0, zIndex: 2 },

  thMetric:        { background: PL_TH_BG, color: PL_TH_TEXT, padding: "14px 20px", fontWeight: 600, fontSize: 10, textAlign: "left",   whiteSpace: "nowrap", minWidth: 180, position: "sticky", left: 0, zIndex: 4, letterSpacing: "0.08em", textTransform: "uppercase", verticalAlign: "middle", borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}` },
  thBandActuals:   { background: PL_TH_BG, color: PL_TH_TEXT, padding: "12px 10px", fontWeight: 600, fontSize: 10, textAlign: "center", letterSpacing: "0.06em", textTransform: "uppercase", borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}` },
  thBandForecast:  { background: PL_TH_BG, color: PL_TH_TEXT, padding: "12px 10px", fontWeight: 600, fontSize: 10, textAlign: "center", letterSpacing: "0.06em", textTransform: "uppercase", borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}` },
  thBandTotal:     { background: PL_TH_BG, color: PL_TH_TEXT, padding: "12px 10px", fontWeight: 600, fontSize: 10, textAlign: "center", letterSpacing: "0.06em", textTransform: "uppercase", minWidth: 88, verticalAlign: "middle", borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}` },
  thMonthActual:   { background: ACTUAL_TH_BG, color: ACTUAL_TH_FG, padding: "12px 10px", fontWeight: 500, fontSize: 12, textAlign: "right",  minWidth: 72, whiteSpace: "nowrap", borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}` },
  thMonthForecast: { background: PL_TH_BG, color: PL_TH_TEXT, padding: "12px 10px", fontWeight: 500, fontSize: 12, textAlign: "right",  minWidth: 72, whiteSpace: "nowrap", borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}` },
  thColTotal:      { background: PL_TH_BG, color: PL_TH_TEXT, padding: "12px 10px", fontWeight: 600, fontSize: 12, textAlign: "right",  minWidth: 88, whiteSpace: "nowrap", borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}` },

  tdLabel:    { padding: "13px 20px", whiteSpace: "nowrap", borderBottom: `1px solid ${HAIRLINE}`, fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, position: "sticky", left: 0, zIndex: 1, background: "#FFFFFF", boxShadow: "6px 0 12px -8px rgba(0,0,0,0.06)" },
  tdActual:   { padding: "13px 14px", textAlign: "right", color: "#374151",  borderBottom: `1px solid ${HAIRLINE}`, fontSize: 13, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", background: "#FFFFFF" },
  tdForecast: { padding: "13px 14px", textAlign: "right", color: FORECAST_C, borderBottom: `1px solid ${HAIRLINE}`, fontSize: 13, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", background: "#FFFFFF" },
  tdTotal:    { padding: "13px 14px", textAlign: "right", color: TEXT_PRIMARY, borderBottom: `1px solid ${HAIRLINE}`, fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", background: "#FFFFFF" },
}));

/* ── ViewToggle ──────────────────────────────────────────────── */
function ViewToggle({ viewMode, onChange, classes }) {
  const isQuarter = viewMode === "Quarter";
  return (
    <div className={classes.toggleWrap}>
      <span className={classes.toggleLabel} style={{ color: !isQuarter ? PL_TH_BG : "#94A3B8" }}>Month</span>
      <div className={classes.toggleTrack} onClick={() => onChange(isQuarter ? "Month" : "Quarter")} title="Toggle Month / Quarter">
        <div className={classes.toggleThumb} style={{ left: isQuarter ? 24 : 2 }} />
      </div>
      <span className={classes.toggleLabel} style={{ color: isQuarter ? PL_TH_BG : "#94A3B8" }}>Quarter</span>
    </div>
  );
}

/* ── MultiSelect ─────────────────────────────────────────────── */
function MultiSelect({ label, options, selected, onChange, classes }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const allSel  = selected.length === options.length;
  const noneSel = selected.length === 0;
  const display = noneSel ? `Select ${label}` : allSel ? `All ${label}` : `${selected.length} of ${options.length} selected`;

  return (
    <div ref={ref} className={classes.msWrap}>
      <div className={classes.msTrigger} onClick={() => setOpen((o) => !o)}>
        <span className={classes.msTriggerLabel}>{label} ›</span>
        <span className={classes.msTriggerValue}>{display}</span>
      </div>
      {open && (
        <div className={classes.msPanel}>
          <label className={classes.msItem} onClick={(e) => { e.stopPropagation(); onChange(allSel ? [] : [...options]); }}>
            <input type="checkbox" checked={allSel} readOnly style={{ accentColor: ACCENT, cursor: "pointer" }} />
            <span style={{ fontWeight: 700 }}>All</span>
          </label>
          <div className={classes.msDivider} />
          {options.map((o) => (
            <label key={o} className={classes.msItem} onClick={(e) => { e.stopPropagation(); onChange(selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o]); }}>
              <input type="checkbox" checked={selected.includes(o)} readOnly style={{ accentColor: ACCENT, cursor: "pointer" }} />
              <span>{o}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}


/* ── MetricTable ─────────────────────────────────────────────── */
function MetricTable({ apiData, lineItem, viewMode, fiscalYear, classes, globalPeriodKeys, filters }) {
  const cutoff = apiData?.actualThroughMonth ?? 0;
  let cols   = viewMode === "Month" ? makeMonthCols(cutoff) : makeQtrCols(cutoff);
  if (globalPeriodKeys?.length) {
    const allowed = new Set(globalPeriodKeys);
    cols = cols.filter((c) => c.isTotal || allowed.has(c.key));
  }

  const periodFiltered = !!globalPeriodKeys?.length;
  const periodCols  = cols.filter((c) => !c.isTotal);
  const actualCount = periodCols.filter((c) => c.isActual).length;
  const fcastCount  = periodCols.length - actualCount;

  const cyData = lineItem ? (apiData?.items?.[lineItem.key]?.cy ?? {}) : {};
  const isPct  = lineItem?.isPct ?? false;
  const cyRowMap = useMemo(() => {
    const items = apiData?.items ?? {};
    return Object.fromEntries(
      Object.entries(items).map(([k, v]) => [k, v?.cy ?? {}]),
    );
  }, [apiData?.items]);

  const cellCls = (c) => {
    if (c.isTotal) return classes.tdTotal;
    return c.isActual ? classes.tdActual : classes.tdForecast;
  };

  return (
    <div className={classes.tableWrap}>
      <table className={classes.table}>
        <thead className={classes.thead}>
          <tr>
            <th className={classes.thMetric} rowSpan={2}>Metric</th>
            {actualCount > 0 && <th className={classes.thBandActuals} colSpan={actualCount}>{fiscalYear} — Actuals</th>}
            {fcastCount  > 0 && <th className={classes.thBandForecast} colSpan={fcastCount}>{fiscalYear} — Forecast</th>}
            <th className={classes.thBandTotal}>Total</th>
          </tr>
          <tr>
            {periodCols.map((c) => (
              <th key={c.key} className={c.isActual ? classes.thMonthActual : classes.thMonthForecast}>{c.label}</th>
            ))}
            <th className={classes.thColTotal}>{periodFiltered ? "Selected" : "Year"}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className={classes.tdLabel}>{lineItem?.label ?? "—"}</td>
            {cols.map((c) => {
              let val = c.isTotal
                ? getPeriodFilteredTotal(cyData, filters, viewMode, {
                  rowKey: lineItem?.key,
                  allRowData: cyRowMap,
                  isPct,
                })
                : cyData[c.key];
              // getPeriodFilteredTotal returns % rows in ×100 (percent) units when a
              // period filter is active (built for sibling tabs that pre-scale %); this
              // tab keeps % as fractions and fmtCell applies ×100, so divide back here.
              if (c.isTotal && isPct && periodFiltered) val = val / 100;
              return (
              <td key={c.key} className={cellCls(c)}>{fmtCell(val, isPct)}</td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/* ── Main component ──────────────────────────────────────────── */
export default function PLMonthlyTrend({ filters, actualsThroughDate }) {
  const plClasses   = usePlByProgramStyles();
  const viewClasses = useStyles();
  useReportFonts();
  const classes = plClasses;

  const [selMetrics, setSelMetrics] = useState([
    "PY",
    "CY",
    "Budget",
    "Cumulative Budget",
  ]);
  const [viewMode,   setViewMode]   = useState("Month");
  const [keyCompare, setKeyCompare] = useState("Revenue");
  const [apiData,    setApiData]    = useState(null);
  const [loading,    setLoading]    = useState(true);

  const apiFilterKey = useMemo(() => getApiFilterKey(filters, buildApiFilters), [filters]);
  const globalPeriodKeys = useMemo(
    () => getGlobalPeriodColumnKeys(filters, viewMode),
    [filters?.monthName, filters?.quarter, viewMode],
  );

  useEffect(() => {
    setLoading(true);
    getPLMonthlyTrendAPI(buildApiFilters(filters))
      .then((res) => {
        if (res?.status === 200 && res.data) setApiData(res.data);
        else setApiData(null);
      })
      .catch(() => setApiData(null))
      .finally(() => setLoading(false));
  }, [apiFilterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const lineItems  = apiData?.lineItems ?? FALLBACK_LINE_ITEMS;
  const lineItem   = lineItems.find((li) => li.label === keyCompare) ?? lineItems[0];
  const kcOptions  = lineItems.map((li) => li.label);
  const fy         = apiData?.fiscalYear ?? new Date().getFullYear();
  const cutoff     = apiData?.actualThroughMonth ?? 0;
  const headerDate = headerActualsDate(
    filters?.year,
    loading,
    formatActualsThroughDate(apiData?.actualThroughMonth),
  );

  const chartOption = buildOption(apiData, lineItem, selMetrics, viewMode, globalPeriodKeys);
  const chartTitle  = `${lineItem?.label ?? ""} — ${viewMode === "Month" ? "Monthly" : "Quarterly"} Trend`;

  return (
    <>
      {loading && <Loading />}
      <div className={classes.root}>
        <div className={classes.container}>
          <PlPageHeader
            classes={classes}
            title="P&L Monthly Trend"
            accent="Trend View"
            meta="CY, PY, and Budget Comparison"
            metaTop={`FY-${fy}`}
            date={headerDate}
          />

          <div className={classes.sectionCard}>
            <div className={classes.filterBar}>
              <div className={viewClasses.infoWrap}>
                <button
                  type="button"
                  className={viewClasses.infoIcon}
                  aria-label="Select metrics to compare in the chart"
                >
                  i
                </button>
                <span className={viewClasses.infoText} role="tooltip">
                  Select metrics to compare in the chart
                </span>
              </div>

              <div className={viewClasses.filterDivider} />

              <MultiSelect
                label="Metrics"
                options={METRIC_OPTS}
                selected={selMetrics}
                onChange={setSelMetrics}
                classes={viewClasses}
              />

              <div className={viewClasses.filterDivider} />

              <ViewToggle viewMode={viewMode} onChange={setViewMode} classes={viewClasses} />

              <div style={{ flex: 1 }} />

              <PlFilterGroup
                label="Key Compares"
                options={kcOptions}
                value={lineItem?.label ?? ""}
                onChange={setKeyCompare}
                classes={classes}
              />
            </div>

            {!loading && (
              <div className={classes.chartPanelStack}>
                <PlChartPanel
                  title={chartTitle}
                  option={chartOption}
                  classes={classes}
                  panelClassName={viewClasses.chartPanelTrend}
                />
              </div>
            )}
          </div>

          {!loading && (
            <div className={viewClasses.tableSection}>
              <div className={viewClasses.tableSectionHead}>
                <h2 className={viewClasses.tableSectionTitle}>{lineItem?.label ?? ""}</h2>
                <div className={viewClasses.tableSectionMeta}>
                  FY-{fy} · {viewMode === "Month" ? "Monthly" : "Quarterly"} Breakdown
                </div>
              </div>
              <MetricTable
                apiData={apiData}
                lineItem={lineItem}
                viewMode={viewMode}
                fiscalYear={fy}
                classes={viewClasses}
                globalPeriodKeys={globalPeriodKeys}
                filters={filters}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
